import { http } from "@/lib/http";

// Wire types for /workspaces/:id/synthesis — canonical home for these DTOs
// (re-exported by @/features/synthesis/types for feature-side consumers).
export type SynthesisMode = "person" | "team";

export interface SynthesisStructuredRef {
  objectType: string;
  objectId: string;
  label?: string;
}

export interface SynthesisReportContent {
  narrative: string;
  highlights: string[];
  actions: string[];
  structuredRefs: SynthesisStructuredRef[];
  facts?: string[];
}

export interface SynthesisReport {
  section: string;
  content: SynthesisReportContent;
  generatedAt: string;
}

export interface SynthesisResponse {
  mode: SynthesisMode;
  reports: SynthesisReport[];
}

export interface SynthesisCollectFlags {
  email: boolean;
  calendar: boolean;
  chat: boolean;
}

export interface SynthesisBriefingConfig {
  feeds: string[];
  headlineTopics: string[];
  emphasis: string;
}

export interface SynthesisPreferences {
  collectFlags: SynthesisCollectFlags;
  briefingConfig: SynthesisBriefingConfig;
  updatedAt?: string;
}

export async function getSynthesis(
  authToken: string | null,
  workspaceId: string,
  opts: { refresh?: boolean; section?: string } = {},
): Promise<SynthesisResponse> {
  const params = new URLSearchParams();
  if (opts.refresh) params.set("refresh", "1");
  if (opts.section) params.set("section", opts.section);
  const qs = params.toString();
  const response = await http.get<SynthesisResponse>(
    `/workspaces/${encodeURIComponent(workspaceId)}/synthesis${qs ? `?${qs}` : ""}`,
    { authToken },
  );
  return response.data;
}

export async function getSynthesisPreferences(
  authToken: string | null,
  workspaceId: string,
): Promise<SynthesisPreferences> {
  const response = await http.get<SynthesisPreferences>(
    `/workspaces/${encodeURIComponent(workspaceId)}/synthesis/preferences`,
    { authToken },
  );
  return response.data;
}

export async function updateSynthesisPreferences(
  authToken: string | null,
  workspaceId: string,
  prefs: SynthesisPreferences,
): Promise<SynthesisPreferences> {
  const response = await http.put<SynthesisPreferences>(
    `/workspaces/${encodeURIComponent(workspaceId)}/synthesis/preferences`,
    prefs,
    { authToken },
  );
  return response.data;
}
